import { useEffect, useState, useCallback, useContext } from 'react';
import { PromiseState } from 'react-refetch';
import delve from 'dlv';

import { getTrendOutlierRemovalConfig } from 'config';
import removeTrendOutliers from 'helpers/removeTrendOutliers';
import useChartDipsParam from 'hooks/useChartDipsParam';
import { EntityContext } from 'context/entityContext';
import { ITrend, IItem } from './../types';

/**
 * useTrendData removes outliers and sort the trends and trend data to an order
 * that is expected by the LineChart.
 *
 * @param {array} trendData Trend data to prepare for rendering
 */
const useTrendData = (trendData: PromiseState<ITrend[]>): ITrend[] => {
  const [trends, setTrends] = useState([]);

  const ctx = useContext(EntityContext);
  const { compareMode, countryIds, entityIds } = ctx;
  const isChartDipsOn = useChartDipsParam();

  /**
   * Callback for Array.prototype.sort(). Sorts trends in the right order,
   * according to the compare mode.
   * @param {object} a Trend A
   * @param {object} b Trend B
   * @returns {(-1|0|1)} Sorting instruction
   */
  const byCompareMode = useCallback(
    (a, b) => {
      const getIndex =
        (arr: (number | string)[], accessor: string) =>
        (el: any): number => {
          return arr.indexOf(delve(el, accessor));
        };

      if (compareMode === 'entity') {
        const getEntityIdx = getIndex(entityIds, 'entity.id');
        return getEntityIdx(a) - getEntityIdx(b);
      }

      if (compareMode === 'country') {
        const getCountryIdx = getIndex(countryIds as number[], 'countryId');
        return getCountryIdx(a) - getCountryIdx(b);
      }

      return 0;
    },
    [compareMode, countryIds, entityIds]
  );

  /**
   * Prepare the trend data when it comes in and set it to state.
   */
  useEffect(() => {
    if (trendData.fulfilled) {
      const trends = trendData?.value
        ?.map((trend: ITrend): ITrend => {
          return removeOutliers(trend, isChartDipsOn);
        })
        .sort(byCompareMode)
        .map(sortDataDescendingBy('periodKey'));

      setTrends(trends as any);
    }
  }, [byCompareMode, trendData, isChartDipsOn]);

  return trends;
};

export default useTrendData;

/**
 * Removes the outliers from the end of a trend's data array.
 * @param {object} trend Trend to remove outliers from
 * @returns {object}
 */
function removeOutliers(trend: ITrend, isChartDipsOn: boolean): ITrend {
  return {
    ...trend,
    data: removeTrendOutliers({
      isChartDipsOn,
      source: trend.data.slice(),
      keyToCheck: 'value',
      ...getTrendOutlierRemovalConfig(''), // default config - amountOfItemsToCheck: 1, amountOfItemsToCompareAgainst: 3
    }),
  };
}

/**
 * Sorts trend data arrays descending by [key].
 * @param {string} key
 */
function sortDataDescendingBy(key: string): any {
  return (trend: ITrend): ITrend => {
    return {
      ...trend,
      data: trend.data.sort((a: IItem, b: IItem) => b[key] - a[key]),
    };
  };
}
